import styled from 'styled-components'
import { Tokens } from '@/assets/tokens'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  background: linear-gradient(135deg, ${Tokens.ColorBackground} 0%, #f8fafb 100%);
  border-radius: 16px;
  min-height: 400px;
`

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, ${Tokens.ColorWhite} 0%, #fdfdfd 100%);
  border-radius: 16px;
  border: 1px solid ${Tokens.ColorBorder};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
`

export const HeaderTitle = styled.h3`
  margin: 0;
  color: ${Tokens.ColorTextPrimary};
  font-size: 24px;
  font-weight: 600;
  background: linear-gradient(135deg, ${Tokens.ColorTextPrimary} 0%, #4a5568 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

export const NewTestButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  background: linear-gradient(135deg, ${Tokens.ColorUIPrimary} 0%, #00d4aa 100%);
  color: ${Tokens.ColorWhite};
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(1, 195, 141, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(1, 195, 141, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(1, 195, 141, 0.35);
  }

  span {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
  }
`

export const ModalSection = styled.div<{ $show: boolean }>`
  display: ${({ $show }) => ($show ? 'block' : 'none')};
  position: relative;
  width: 100%;
  margin: 24px 0;
  padding: 0;
`

export const ModalCard = styled.div`
  background: linear-gradient(135deg, ${Tokens.ColorWhite} 0%, #fdfdfd 100%);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid ${Tokens.ColorBorder};
  padding: 32px;
  margin: 0 auto;
  max-width: 500px;
  position: relative;
  animation: slideInFromTop 0.3s ease-out;

  @keyframes slideInFromTop {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`

export const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${Tokens.ColorTextPrimary};
`

export const ModalSubtitle = styled.p`
  margin: 8px 0 0 0;
  color: ${Tokens.ColorTextSecondary};
  font-size: 14px;
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  color: ${Tokens.ColorTextSecondary};

  &:hover {
    background-color: ${Tokens.ColorHover};
    color: ${Tokens.ColorTextPrimary};
  }
`

export const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
`

export const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 400px;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  width: 100%;

  /* Force DatePicker to use full width */
  > div {
    max-width: 100% !important;
    min-width: 100% !important;
  }
`

export const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${Tokens.ColorTextPrimary};
`

export const ModalActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  margin-top: 8px;

  button {
    flex: 1;
    max-width: 180px;
  }
`

export const TestItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const SectionTitle = styled.h4`
  margin: 0;
  color: ${Tokens.ColorTextPrimary};
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.1);
    }
  }

  * {
    animation-name: pulse;
  }
`

export const TestItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 20px;
  width: 100%;
`

export const TestItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, ${Tokens.ColorWhite} 0%, #fdfdfd 100%);
  border: 1px solid ${Tokens.ColorBorder};
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, ${Tokens.ColorUIPrimary}, #00d4aa);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    border-color: ${Tokens.ColorUIPrimary};
    box-shadow: 0 8px 32px rgba(1, 195, 141, 0.15);
    transform: translateY(-2px);

    &::before {
      transform: scaleX(1);
    }
  }
`

export const TestItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`

export const TestItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`

export const TestItemTitle = styled.div`
  font-weight: 700;
  color: ${Tokens.ColorTextPrimary};
  font-size: 18px;
  line-height: 1.4;
  letter-spacing: -0.2px;
`

export const TestItemDescription = styled.div`
  font-size: 14px;
  color: ${Tokens.ColorTextSecondary};
  line-height: 1.5;
  font-weight: 500;
`

export const TestItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${Tokens.ColorTextTertiary};
  margin-top: 6px;
  font-weight: 400;
`

export const StatusBadge = styled.span<{ $status: string }>`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  white-space: nowrap;
  background: ${({ $status }) =>
    $status === 'PROCESSING' ? `linear-gradient(135deg, ${Tokens.ColorUIWarn} 0%, #f6ad55 100%)`
    : $status === 'FINISHED' ? `linear-gradient(135deg, ${Tokens.ColorUIPrimary} 0%, #00d4aa 100%)`
    : `linear-gradient(135deg, ${Tokens.ColorUIDanger} 0%, #fc8181 100%)`};
  color: white;
  box-shadow: 0 3px 10px
    ${({ $status }) =>
      $status === 'PROCESSING' ? 'rgba(237, 137, 54, 0.3)'
      : $status === 'FINISHED' ? 'rgba(1, 195, 141, 0.3)'
      : 'rgba(229, 62, 62, 0.3)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 80px 40px;
  text-align: center;
  background: linear-gradient(135deg, ${Tokens.ColorWhite} 0%, #fdfdfd 100%);
  border: 2px dashed ${Tokens.ColorBorder};
  border-radius: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(1, 195, 141, 0.03) 0%, transparent 70%);
    animation: float 6s ease-in-out infinite;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) rotate(0deg);
    }
    50% {
      transform: translateY(-10px) rotate(1deg);
    }
  }
`

export const EmptyStateTitle = styled.h4`
  margin: 0;
  color: ${Tokens.ColorTextSecondary};
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.2px;
`

export const EmptyStateDescription = styled.p`
  margin: 0;
  color: ${Tokens.ColorTextTertiary};
  font-size: 15px;
  line-height: 1.6;
  max-width: 400px;
  font-weight: 400;
`
